"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.analyseAiJsonCompliance = analyseAiJsonCompliance;
const zod_1 = __importDefault(require("zod"));
function unwrapModifiers(schema) {
    let optional = false;
    let nullable = false;
    let s = schema;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (s instanceof zod_1.default.ZodOptional || s instanceof zod_1.default.ZodExactOptional) {
            optional = true;
            s = s.unwrap();
            continue;
        }
        if (s instanceof zod_1.default.ZodNullable) {
            nullable = true;
            s = s.unwrap();
            continue;
        }
        if (s instanceof zod_1.default.ZodDefault || s instanceof zod_1.default.ZodPrefault) {
            s = s.unwrap();
            continue;
        }
        break;
    }
    return { optional, nullable, inner: s };
}
function collectEmptyRequiredStrings(schema, value, path, out) {
    const { optional, nullable, inner } = unwrapModifiers(schema);
    if (optional) {
        if (value === undefined) {
            return;
        }
    }
    if (nullable && value === null) {
        return;
    }
    if (inner instanceof zod_1.default.ZodObject) {
        const obj = value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)
            ? value
            : null;
        const shape = inner.shape;
        for (const key of Object.keys(shape)) {
            const nextPath = [...path, key];
            try {
                collectEmptyRequiredStrings(shape[key], obj === null || obj === void 0 ? void 0 : obj[key], nextPath, out);
            }
            catch {
                // Defensive: never break the caller on unexpected schema shapes
            }
        }
        return;
    }
    if (inner instanceof zod_1.default.ZodArray) {
        const arr = Array.isArray(value) ? value : [];
        const el = inner.element;
        arr.forEach((item, i) => {
            try {
                collectEmptyRequiredStrings(el, item, [...path, String(i)], out);
            }
            catch {
                // ignore
            }
        });
        return;
    }
    if (inner instanceof zod_1.default.ZodString) {
        if (!optional && !nullable && typeof value === 'string') {
            if (value === '' || value === 'null') {
                out.push({ path: path.join('.') || '(root)', value });
            }
        }
    }
}
/**
 * Validates JSON returned by OpenAI (or similar) against a Zod object schema:
 * - Runs `safeParse` and collects Zod issues
 * - For required, non-nullable strings, flags `""` or the literal `"null"` (LLM artifact)
 *
 * Wrapped defensively so analysis never throws to the caller.
 */
function analyseAiJsonCompliance(schema, data) {
    const issues = [];
    try {
        const parsed = schema.safeParse(data);
        if (!parsed.success) {
            issues.push({ kind: 'zod', issues: parsed.error.issues });
        }
        try {
            const emptyStrings = [];
            collectEmptyRequiredStrings(schema, data, [], emptyStrings);
            for (const e of emptyStrings) {
                issues.push({
                    kind: 'empty_required_string',
                    path: e.path,
                    value: e.value,
                });
            }
        }
        catch (e) {
            issues.push({
                kind: 'internal',
                message: `empty-string walk: ${e instanceof Error ? e.message : String(e)}`,
            });
        }
    }
    catch (e) {
        issues.push({
            kind: 'internal',
            message: `compliance: ${e instanceof Error ? e.message : String(e)}`,
        });
    }
    return issues;
}
